<template>
  <full-screen-loader v-if="loading" :white-bg="true"/>
  <smd-one-col-layout v-else :class="computedClass"
                      :content-styles="contentStylesClass">
    <smd-header class="root-messages__header top-bar-box-shadow"
                slot="header"
                v-if="showTab">
      <img alt="nowserving logo"
           class="crisp-image"
           src="@/assets/images/nowserving.png"
           slot="left-actions"
           v-if="$isMobile"
           width="123px">
      <smd-fixed-tab-panel slot="left-actions"
                           v-else
                           :options="$options.tabs"/>
      <smd-btn class="icon-btn"
               icon="ns-icon-settings"
               slot="right-actions"
               @click="goToSettings">
      </smd-btn>
    </smd-header>
    <smd-fixed-tab-panel slot="toolbar"
                         v-if="showTab && $isMobile"
                         :options="$options.tabs"/>
    <router-view v-bind="bindedValues"/>
  </smd-one-col-layout>
</template>

<script>
  import {mapActions, mapGetters} from 'vuex';
  import Routes from '@/router/routes';
  import HeaderFunctionMixin from '@/mixins/header-functions-mixin';
  import {
    ACTION_ADD_DOCTOR_LATEST_MESSAGES,
    ACTION_SET_MESSAGES_STATE,
    ACTION_SET_BILLS_STATE,
    GETTER_DOCTOR_PATIENT_PARTNER,
    GETTER_TOKENS,
    GETTER_ONGOING_MEDICINE_ORDERS,
    GETTER_PAST_MEDICINE_ORDERS,
    GETTER_DOCTOR_LATEST_MESSAGES,
    GETTER_MESSAGES_STATE,
    GETTER_BILLS_STATE,
    GETTER_UNSETTLED_BILLS,
    GETTER_SETTLED_BILLS,
  } from '@/store/types';
  import ToastMixin from '@/mixins/toast-mixin';
  import {
    toPhilMoment,
  } from '@/utils/dates';
  import {DATE_PROP_MSG_TYPE} from '@/constants/inbox-messages';
  import GetElementStringMixin from '@/mixins/get-element-string';
  import {BillsService} from '@/services/bills';
  import {BILL_TYPES} from '@/reference/bills';
  import {DEFAULT_PHARMACIES} from '@/reference/medicine';
  import OrderMessageParserMixin from '@/mixins/messages/order-message-parser-mixin';
  import {MessageService} from '@/services/messages';

  export default {
    name: 'root-messages',
    mixins: [
      HeaderFunctionMixin,
      ToastMixin,
      GetElementStringMixin,
      OrderMessageParserMixin,
    ],
    computed: {
      ...mapGetters({
        getDoctorPatientPair: GETTER_DOCTOR_PATIENT_PARTNER,
        tokens: GETTER_TOKENS,
        onGoingOrders: GETTER_ONGOING_MEDICINE_ORDERS,
        archivedOrders: GETTER_PAST_MEDICINE_ORDERS,
        doctorMessages: GETTER_DOCTOR_LATEST_MESSAGES,
        messagesState: GETTER_MESSAGES_STATE,
        billsState: GETTER_BILLS_STATE,
        unsettledBills: GETTER_UNSETTLED_BILLS,
        settledBills: GETTER_SETTLED_BILLS,
      }),
      loading() {
        return (!this.messagesState.initialized && this.messagesState.fetching)
            || (!this.billsState.initialized && this.billsState.fetching);
      },
      contentStylesClass() {
        return [{'dark-light-bg': !this.$isMobile && !this.onChatWindow}, {'root-messages__content': !this.onChatWindow}]
      },
      computedClass() {
        return {
          'full-width': this.onChatWindow,
        };
      },
      showTab() {
        return !this.onChatWindow;
      },
      onChatWindow() {
        return [
          Routes.CHAT_ROUTE,
          Routes.ORDER_CHAT_ROUTE,
        ].includes(this.$route.name);
      },
      doctorChatList() {
        if (!this.getDoctorPatientPair) return [];

        return Object.keys(this.getDoctorPatientPair)
          .map((pairToken) => {
            const {localStorageKey} = this.getDoctorPatientPair[pairToken];
            const latestMessage = this.doctorMessages[localStorageKey];
            const messageIsoStringDate = latestMessage
              ? latestMessage[DATE_PROP_MSG_TYPE[latestMessage.msgtype]]
              : null;

            return {
              message: latestMessage,
              pairToken,
              messageIsoStringDate,
            };
          });
      },
      sortedUnsettledBills() {
        return this.unsettledBills
          .slice(0)
          .sort((billA, billB) => {
            if (toPhilMoment(billB.messageIsoStringDate).valueOf() > toPhilMoment(billA.messageIsoStringDate).valueOf()) {
              return 1;
            }

            if (toPhilMoment(billB.messageIsoStringDate).valueOf() < toPhilMoment(billA.messageIsoStringDate).valueOf()) {
              return -1;
            }

            return 0;
          });
      },
      bindedValues() {
        const orderFilter = (({partnercode}) => partnercode === DEFAULT_PHARMACIES.NS_PHARMACY);
        const chatList = [
          ...this.doctorChatList,
          ...this.onGoingOrders
            .filter(orderFilter)
            .map((order) => ({
              order,
              messageIsoStringDate: order.lastMessageDate,
            })),
          ...this.settledBills,
        ]
          .slice(0)
          .sort((messageA, messageB) => {
              if (toPhilMoment(messageB.messageIsoStringDate).valueOf() > toPhilMoment(messageA.messageIsoStringDate).valueOf()) {
                return 1;
              }

              if (toPhilMoment(messageB.messageIsoStringDate).valueOf() < toPhilMoment(messageA.messageIsoStringDate).valueOf()) {
                return -1;
              }

              return 0;
            }
          );

        chatList.unshift(...this.sortedUnsettledBills);
        chatList.push(
          ...this.archivedOrders
            .filter(orderFilter)
            .map((order) => ({
              order,
              messageIsoStringDate: order.lastMessageDate,
            })),
        );

        return {
          chatList,
        };
      },
    },
    watch: {
      tokens: {
        handler(newValue) {
          if (newValue) {
            if (!this.messagesState.initialized) {
              this.fetchMessages();
            }
            if (!this.billsState.initialized) {
              this.getBills();
            }
          }
        },
        immediate: true,
      },
      'billsState.initialized': {
        handler(initialized) {
          if (!initialized && this.tokens && !this.onChatWindow) {
            this.getBills();
          }
        },
      },
      onChatWindow(isChatWindow) {
        if (!isChatWindow && !this.billsState.initialized && this.tokens) {
          this.getBills();
        }
      },
    },
    methods: {
      ...mapActions({
        addDoctorLastMessage: ACTION_ADD_DOCTOR_LATEST_MESSAGES,
        setMessagesState: ACTION_SET_MESSAGES_STATE,
        setBillsState: ACTION_SET_BILLS_STATE,
      }),
      async fetchMessages() {
        this.error = null;
        this.setMessagesState({fetching: true});

        const tokenPairs = Object.keys(this.getDoctorPatientPair).map((accesstoken) => accesstoken);
        const lasttimestamp = (new Date()).getTime();
        const doctorMessagesRequests = tokenPairs.map((accesstoken) => MessageService.getMessagesCombined(lasttimestamp, accesstoken));

        try {
          const doctorMessagesResponses = await Promise.all(doctorMessagesRequests);

          doctorMessagesResponses.forEach((response, index) => {
            const pairToken = tokenPairs[index];
            const {localStorageKey} = this.getDoctorPatientPair[pairToken];
            this.addDoctorLastMessage({
              [localStorageKey]: response.data[0],
            });
          });

          this.setMessagesState({fetching: false, initialized: true});
        } catch (error) {
          this.setMessagesState({fetching: false});
          this.error = 'Failed to fetch doctor messages. Please try again later.';
        }
      },
      async getBills() {
        this.setBillsState({fetching: true});

        try {
          const requests = this.tokens.map(({accesstoken}) => BillsService.getBill(accesstoken));
          const billsResponses = await Promise.all(requests);
          const unsettledBills = [];
          const settledBills = [];

          billsResponses.forEach((response, index) => {
            response.data.forEach((bill) => {
              if (bill.status === BILL_TYPES.VOID) {
                return;
              }

              const newBill = {
                bill,
                messageIsoStringDate: bill.billDate,
                patient: this.tokens[index].patient,
              };

              if (bill.status === BILL_TYPES.UNSETTLED) {
                unsettledBills.push(newBill);
              } else {
                settledBills.push(newBill);
              }
            });
          });

          this.setBillsState({
            fetching: false,
            unsettledBills,
            settledBills,
            initialized: true,
          });
        } catch (error) {
          this.setBillsState({fetching: false});
          this.showToast({
            text: 'Failed to fetch bills. Please try again later.',
            theme: 'danger',
          });
        }
      },
    },
    tabs: [
      {
        name: 'All Messages',
        route: Routes.MESSAGES_ROUTE_ALL,
        to: {
          name: Routes.MESSAGES_ROUTE_ALL
        }
      },
      {
        name: 'Doctors',
        route: Routes.DOCTOR_MESSAGES_ROUTE,
        to: {
          name: Routes.DOCTOR_MESSAGES_ROUTE
        },
      },
      {
        name: 'Services',
        route: Routes.SERVICE_MESSAGES_ROUTE,
        to: {
          name: Routes.SERVICE_MESSAGES_ROUTE,
        },
      },
    ],
  }
</script>

<style lang="scss">
  @import "~@/assets/styles/util.scss";

  .root-messages__header {
    @include responsive(tablet, "ipad") {
      .smd-fixed-tab-panel {
        border: none;
        margin-bottom: -14px;
      }

      .smd-fixed-tab-panel__item.selected:after {
        bottom: -4px;
      }
    }
    @include responsive(desktop) {

      .smd-fixed-tab-panel {
        border: none;
        margin-bottom: -14px;
      }

      .smd-fixed-tab-panel__item.selected:after {
        bottom: -4px;
      }
    }
  }

  .root-messages__content {
    > div {
      @include set-max-width($max-width: 1583px);
    }

    @include responsive('min-width: 1024px') {
      padding: space(xxl) !important;
    }
  }
</style>